import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { updateDoc, doc, getDoc } from 'firebase/firestore';
import { GetDB } from '../../../utils/database/firebase-budget-tool';
import PassportList from '../passport/passportList';
import CountryWeightInput from '../passport/countryWeightInput';

const CountryDetails = ({ country, passports, onClose }) => {
    const [unsavedChanges, setUnsavedChanges] = useState({
        information: country.information || '',
        internalWeight: country.internalWeight || 1,
        noVisaRequired: country.noVisaRequired || [],
        visaOnArrival: country.visaOnArrival || [],
        eVisaRequired: country.eVisaRequired || [],
        paperVisaRequired: country.paperVisaRequired || [],
        restrictedPassports: country.restrictedPassports || []
    });

    const [hasChanges, setHasChanges] = useState(false);

    const db = GetDB();

    useEffect(() => {
        console.log('=== COUNTRY PROP CHANGED ===');
        console.log('New country data:', {
            id: country.id,
            country: country.country,
            internalWeight: country.internalWeight,
            information: country.information
        });
        
        // Update local state when country prop changes
        const newState = {
            information: country.information || '',
            internalWeight: country.internalWeight || 1,
            noVisaRequired: country.noVisaRequired || [],
            visaOnArrival: country.visaOnArrival || [],
            eVisaRequired: country.eVisaRequired || [],
            paperVisaRequired: country.paperVisaRequired || [],
            restrictedPassports: country.restrictedPassports || []
        };
        
        console.log('Setting new unsaved changes:', newState);
        setUnsavedChanges(newState);
        setHasChanges(false);
    }, [country]);

    const handleUpdate = async (field, value) => {
        console.log('=== HANDLE UPDATE ===');
        console.log('Update params:', { 
            field, 
            value, 
            currentWeight: country.internalWeight,
            currentInformation: country.information,
            currentUnsavedChanges: unsavedChanges
        });
        
        if (field === 'internalWeight') {
            // Ensure weight is a number between 0 and 100
            const weight = Math.max(0, Math.min(100, Number(value)));
            console.log('Updating weight:', { 
                oldWeight: country.internalWeight, 
                newWeight: weight,
                type: typeof weight,
                currentUnsavedWeight: unsavedChanges.internalWeight
            });
            
            // Update local state only
            setUnsavedChanges(prev => {
                const newState = {
                    ...prev,
                    internalWeight: weight
                };
                console.log('New unsaved changes state:', {
                    before: prev,
                    after: newState
                });
                return newState;
            });
            setHasChanges(true);
            console.log('Set hasChanges to true');
        } else if (field === 'information') {
            console.log('Updating information:', {
                oldInfo: country.information,
                newInfo: value,
                type: typeof value,
                currentUnsavedInfo: unsavedChanges.information
            });
            
            // Update local state only
            setUnsavedChanges(prev => {
                const newState = {
                    ...prev,
                    information: value
                };
                console.log('New unsaved changes state:', {
                    before: prev,
                    after: newState
                });
                return newState;
            });
            setHasChanges(true);
            console.log('Set hasChanges to true');
        } else {
            setUnsavedChanges(prev => {
                const newState = {
                    ...prev,
                    [field]: value
                };
                console.log('New unsaved changes state:', {
                    before: prev,
                    after: newState
                });
                return newState;
            });
            setHasChanges(true);
            console.log('Set hasChanges to true');
        }
    };

    const handlePassportSelect = async (option, fieldName, country) => {
        if (!option) return;
        
        console.log('Selecting passport:', { option, fieldName, currentCountry: country });
        
        // Check if the passport is already in any visa requirement list
        const isInAnyList = ['noVisaRequired', 'visaOnArrival', 'eVisaRequired', 'paperVisaRequired', 'restrictedPassports']
            .some(list => country[list]?.includes(option.value));
        
        if (isInAnyList) {
            alert('This passport is already assigned to a visa requirement category');
            return;
        }

        const currentList = country[fieldName] || [];
        if (!currentList.includes(option.value)) {
            // Update the local state only
            const newList = [...currentList, option.value];
            setUnsavedChanges(prev => ({
                ...prev,
                [fieldName]: newList
            }));
            setHasChanges(true);
            
            // Update the country prop to reflect changes immediately in UI
            country[fieldName] = newList;
        }
    };

    const handlePassportRemove = async (passportId, fieldName, country) => {
        const currentList = country[fieldName] || [];
        const newList = currentList.filter(id => id !== passportId);
        
        // Update the local state only
        setUnsavedChanges(prev => ({
            ...prev,
            [fieldName]: newList
        }));
        setHasChanges(true);
        
        // Update the country prop to reflect changes immediately in UI
        country[fieldName] = newList;
    };

    const handleInformationChange = (e) => {
        console.log('=== HANDLE INFORMATION CHANGE ===');
        if (!e || !e.target) return;
        const newValue = e.target.value || '';
        console.log('New information value:', newValue);
        handleUpdate('information', newValue);
    };

    const handleSaveChanges = async () => {
        console.log('=== SAVE CHANGES DEBUG ===');
        console.log('Save button clicked, hasChanges:', hasChanges);
        console.log('Current country state:', {
            id: country.id,
            country: country.country,
            internalWeight: country.internalWeight,
            information: country.information,
            region: country.region
        });
        
        console.log('Unsaved changes:', unsavedChanges);
        
        if (!hasChanges) {
            console.log('No changes detected, skipping save');
            return;
        }

        try {
            // Construct the document path
            const docPath = `data/countries_${country.region.toLowerCase()}`;
            console.log('Document path:', docPath);
            
            const countryRef = doc(db, docPath);
            
            // First, let's verify the document exists and get its current data
            const docSnapshot = await getDoc(countryRef);
            if (!docSnapshot.exists()) {
                console.error('Document does not exist:', docPath);
                return;
            }
            
            const currentData = docSnapshot.data();
            console.log('Current document data:', currentData);
            
            // Construct the updates object
            const updates = {
                [`countries.${country.id}`]: {
                    ...currentData.countries?.[country.id],
                    internalWeight: Number(unsavedChanges.internalWeight),
                    information: unsavedChanges.information,
                    noVisaRequired: unsavedChanges.noVisaRequired,
                    visaOnArrival: unsavedChanges.visaOnArrival,
                    eVisaRequired: unsavedChanges.eVisaRequired,
                    paperVisaRequired: unsavedChanges.paperVisaRequired,
                    restrictedPassports: unsavedChanges.restrictedPassports
                }
            };
            
            console.log('Updates object:', {
                path: `countries.${country.id}`,
                updates: updates[`countries.${country.id}`]
            });

            try {
                console.log('Attempting database update...');
                await updateDoc(countryRef, updates);
                console.log('Database update successful');
                
                // Update the country prop with the new values
                Object.entries(unsavedChanges).forEach(([field, value]) => {
                    if (field === 'internalWeight') {
                        country[field] = Number(value);
                    } else {
                        country[field] = value;
                    }
                });
                
                // Reset local state
                setUnsavedChanges({
                    information: country.information || '',
                    internalWeight: Number(country.internalWeight) || 1,
                    noVisaRequired: country.noVisaRequired || [],
                    visaOnArrival: country.visaOnArrival || [],
                    eVisaRequired: country.eVisaRequired || [],
                    paperVisaRequired: country.paperVisaRequired || [],
                    restrictedPassports: country.restrictedPassports || []
                });
                setHasChanges(false);
                console.log('=== SAVE COMPLETE ===');
            } catch (updateError) {
                console.error('Error during updateDoc:', {
                    error: updateError,
                    code: updateError.code,
                    message: updateError.message,
                    stack: updateError.stack
                });
                throw updateError;
            }
        } catch (error) {
            console.error('Error saving changes:', {
                error,
                code: error.code,
                message: error.message,
                stack: error.stack
            });
        }
    };

    const hasUnsavedChanges = () => {
        // Check basic fields
        if (unsavedChanges.information !== country.information || 
            unsavedChanges.internalWeight !== country.internalWeight) {
            return true;
        }

        // Check visa requirement arrays
        const visaFields = ['noVisaRequired', 'visaOnArrival', 'eVisaRequired', 'paperVisaRequired', 'restrictedPassports'];
        for (const field of visaFields) {
            const currentArray = country[field] || [];
            const unsavedArray = unsavedChanges[field] || [];
            
            // Check if arrays are different lengths
            if (currentArray.length !== unsavedArray.length) {
                return true;
            }
            
            // Check if arrays have different contents
            if (!currentArray.every(id => unsavedArray.includes(id)) || 
                !unsavedArray.every(id => currentArray.includes(id))) {
                return true;
            }
        }

        return false;
    };

    return (
        <CountryDetailsPanel>
            <CountryDetailsHeader>
                <div>
                    <h3>{country.country}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <SaveButton 
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Save button clicked');
                                handleSaveChanges();
                            }}
                            hasChanges={hasChanges}
                            type="button"
                        >
                            Save Changes
                        </SaveButton>
                        <IsoCode>ISO: {country.id}</IsoCode>
                        <RegionCode>Region: {country.region}</RegionCode>
                    </div>
                </div>
                <CloseButton onClick={onClose}>×</CloseButton>
            </CountryDetailsHeader>
            <CountryDetailsContent>
                <CountryWeightInput
                    internalWeight={unsavedChanges.internalWeight}
                    onChange={(value) => handleUpdate('internalWeight', value)}
                />
                <InformationSection>
                    <h6>Information</h6>
                    <InformationTextArea
                        value={unsavedChanges.information}
                        onChange={handleInformationChange}
                        placeholder="Add information about the country..."
                    />
                </InformationSection>
                <PassportList 
                    title="No Visa Required" 
                    fieldName="noVisaRequired" 
                    passportIds={country.noVisaRequired}
                    country={country}
                    passports={passports}
                    onPassportSelect={handlePassportSelect}
                    onPassportRemove={handlePassportRemove}
                />
                <PassportList 
                    title="Visa On Arrival" 
                    fieldName="visaOnArrival" 
                    passportIds={country.visaOnArrival}
                    country={country}
                    passports={passports}
                    onPassportSelect={handlePassportSelect}
                    onPassportRemove={handlePassportRemove}
                />
                <PassportList 
                    title="eVisa/ETA Required" 
                    fieldName="eVisaRequired" 
                    passportIds={country.eVisaRequired}
                    country={country}
                    passports={passports}
                    onPassportSelect={handlePassportSelect}
                    onPassportRemove={handlePassportRemove}
                />
                <PassportList 
                    title="Paper Visa Required" 
                    fieldName="paperVisaRequired" 
                    passportIds={country.paperVisaRequired}
                    country={country}
                    passports={passports}
                    onPassportSelect={handlePassportSelect}
                    onPassportRemove={handlePassportRemove}
                />
                <PassportList 
                    title="Restricted Passports" 
                    fieldName="restrictedPassports" 
                    passportIds={country.restrictedPassports}
                    country={country}
                    passports={passports}
                    onPassportSelect={handlePassportSelect}
                    onPassportRemove={handlePassportRemove}
                />
            </CountryDetailsContent>
        </CountryDetailsPanel>
    );
};

const CountryDetailsPanel = styled.div`
    width: 500px;
    background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    color: white;
`;

const CountryDetailsHeader = styled.div`
    padding: 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    h3 {
        margin: 0;
        color: white;
        margin-bottom: 8px;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    color: white;
    cursor: pointer;
    padding: 0 8px;
    line-height: 1;

    &:hover {
        color: ${props => props.theme.primaryAccent};
    }
`;

const CountryDetailsContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background: rgba(255, 255, 255, 0.05);
`;

const InformationSection = styled.div`
    margin-bottom: 20px;
    
    h6 {
        margin-bottom: 8px;
        color: white;
    }
`;

const InformationTextArea = styled.textarea`
    width: 100%;
    min-height: 100px;
    padding: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.1);
    resize: vertical;

    &:focus {
        outline: none;
        border-color: ${props => props.theme.primaryAccent};
        box-shadow: 0 0 0 1px ${props => props.theme.primaryAccent};
    }

    &::placeholder {
        color: rgba(255, 255, 255, 0.5);
    }
`;

const SaveButton = styled.button`
    background: ${props => props.hasChanges ? '#fdb506' : 'rgba(255, 255, 255, 0.1)'};
    border: none;
    color: ${props => props.hasChanges ? '#000000' : 'white'};
    padding: 6px 12px;
    border-radius: 4px;
    cursor: ${props => props.hasChanges ? 'pointer' : 'default'};
    font-size: 14px;
    transition: all 0.2s ease;
    font-weight: ${props => props.hasChanges ? '600' : '400'};

    &:hover {
        ${props => props.hasChanges && `
            background: #FFC800;
            transform: translateY(-1px);
        `}
    }

    &:focus {
        outline: none;
    }
`;

const IsoCode = styled.span`
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    font-family: monospace;
    background: rgba(255, 255, 255, 0.1);
    padding: 4px 8px;
    border-radius: 4px;
`;

const RegionCode = styled.span`
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    font-family: monospace;
    background: rgba(255, 255, 255, 0.1);
    padding: 4px 8px;
    border-radius: 4px;
`;

export default CountryDetails; 